
import { Component, Emit, Prop, Vue, Watch } from "vue-property-decorator";
import SearchDropDown from "@/components/form/SearchDropDown.vue";
import RichTextEditorPlaceholder from "@/types/RichTextEditorPlaceholder";
import { NotificationSubjectLineConstants } from "@/types/organization-settings/NotificationSubjectLineSettings";

type SegmentType = "text" | "token";
type TokenKind = "CustomField" | "ManualField" | "AreaCustomField" | "system";

interface Segment {
	type: SegmentType;
	// text segments
	text?: string;
	// token segments
	raw?: string;
	label?: string;
	kind?: TokenKind;
}

/**
 * Shared subject-line editor. Renders inserted placeholders as removable, colour-coded chips
 * (rather than raw {{...}} text) and keeps the underlying value as a {{...}}-tokenised string so
 * it stays compatible with the backend subject resolver. Used by both the per-template subject
 * editor and the tenant-level Notification Subject Line form.
 */
@Component({
	components: {
		"search-dropdown": SearchDropDown,
	},
})
export default class SubjectLineTokenField extends Vue {
	@Prop({ type: String, default: "" }) public value!: string;
	@Prop({ type: Array, default: () => [] }) public placeholders!: RichTextEditorPlaceholder[];
	@Prop({ type: Array, default: () => [] }) public manualFields!: RichTextEditorPlaceholder[];
	@Prop({ type: Array, default: () => [] }) public customFields!: RichTextEditorPlaceholder[];
	// Area custom fields are a SEPARATE picker rather than a different kind for the picker above:
	// the tenant-level form offers both at once, each behind its own feature gate, so a single
	// "which kind?" prop cannot express it. Empty/false by default, so the per-template editor is
	// unaffected.
	@Prop({ type: Array, default: () => [] }) public areaCustomFields!: RichTextEditorPlaceholder[];
	@Prop({ type: Boolean, default: false }) public showAreaCustomFields!: boolean;
	// Token kind for the primary custom-field picker above. Both callers currently use
	// "CustomField"; Area fields have their own picker and always emit "AreaCustomField".
	@Prop({ type: String, default: "CustomField" }) public customFieldTokenKind!: string;
	// Whether the custom-field picker is offered (e.g. gated on custom-field access).
	@Prop({ type: Boolean, default: true }) public showCustomFields!: boolean;
	// Whether the manual-field picker is offered. Defaults true so the per-template editor is
	// unchanged; the tenant-level form binds this to a dedicated feature flag.
	@Prop({ type: Boolean, default: true }) public showManualFields!: boolean;
	// Read-only mode. generic-update-modal binds :readonly and :disabled on every dynamic
	// `component` field, which is how a user without edit permission views a row (the eye icon in
	// generic-table). Both are declared because the modal passes both; without them the attributes
	// fall through onto the root div and silently do nothing, leaving this field the only editable
	// control on an otherwise greyed-out form. RichTextEditor, rendered in the same modal, declares
	// `readonly` for the same reason.
	@Prop({ type: Boolean, default: false }) public readonly!: boolean;
	@Prop({ type: Boolean, default: false }) public disabled!: boolean;

	private segments: Segment[] = [];

	// The subject limit is a single system-wide value, not per-caller, so it is read from the shared
	// constant rather than passed in.
	private get maxLength(): number {
		return NotificationSubjectLineConstants.MaximumCharacterLength;
	}

	// The modal passes readonly and disabled together; honour either.
	private get isReadOnly(): boolean {
		return this.readonly || this.disabled;
	}

	// {{label.Kind-id}} typed tokens, or {{id}} system placeholders.
	private static readonly tokenRegex =
		/\{\{(?:([^{}]+)\.(CustomField|ManualField|AreaCustomField)-([a-zA-Z0-9-]+)|([a-zA-Z0-9]+))\}\}/g;

	private created(): void {
		this.parseValue(this.value);
	}

	@Watch("value")
	private onValueChanged(newValue: string): void {
		if (newValue !== this.buildRaw()) {
			this.parseValue(newValue);
		}
	}

	private parseValue(value: string): void {
		const segments: Segment[] = [];
		const source = value ?? "";
		let lastIndex = 0;
		let match: RegExpExecArray | null;
		SubjectLineTokenField.tokenRegex.lastIndex = 0;

		// eslint-disable-next-line no-cond-assign
		while ((match = SubjectLineTokenField.tokenRegex.exec(source)) !== null) {
			if (match.index > lastIndex) {
				segments.push({ type: "text", text: source.substring(lastIndex, match.index) });
			}
			if (match[4] !== undefined) {
				segments.push({ type: "token", raw: match[0], kind: "system", label: this.systemLabel(match[4]) });
			} else {
				segments.push({ type: "token", raw: match[0], kind: match[2] as TokenKind, label: match[1] });
			}
			lastIndex = match.index + match[0].length;
		}

		if (lastIndex < source.length) {
			segments.push({ type: "text", text: source.substring(lastIndex) });
		}

		// Always keep a trailing text segment so the operator can continue typing after a chip.
		if (segments.length === 0 || segments[segments.length - 1].type !== "text") {
			segments.push({ type: "text", text: "" });
		}

		this.segments = segments;
	}

	private systemLabel(id: string): string {
		const match = this.placeholders.find((p) => p.id === id);
		return match ? match.label : id;
	}

	private buildRaw(): string {
		return this.segments
			.map((seg) => (seg.type === "token" ? seg.raw : seg.text))
			.join("");
	}

	// Emits the rebuilt raw subject string as the `input` payload, so the component works with v-model.
	// The event name is passed explicitly because @Emit() would otherwise derive it from the method
	// name. Call this as emit() with NO arguments — @Emit appends a decorated method's arguments after
	// its return value, so emit($event) would emit (raw, DOMEvent) and give consumers a second payload.
	@Emit("input")
	private emit(): string {
		return this.buildRaw();
	}

	private get rawLength(): number {
		return this.buildRaw().length;
	}

	// Labels are interpolated into the {{label.Kind-id}} token; braces would make it unresolvable,
	// so strip them from the label portion.
	private sanitizeLabel(label: string): string {
		return (label ?? "").replace(/[{}]/g, "");
	}

	private appendToken(segment: Segment): void {
		// Insert before the trailing text segment when it is empty, otherwise append.
		const trailing = this.segments[this.segments.length - 1];
		if (trailing && trailing.type === "text" && !trailing.text) {
			this.segments.splice(this.segments.length - 1, 0, segment);
		} else {
			this.segments.push(segment, { type: "text", text: "" });
		}
		this.emit();
	}

	private addPlaceholder(placeholder: RichTextEditorPlaceholder): void {
		this.appendToken({ type: "token", kind: "system", raw: `{{${placeholder.id}}}`, label: placeholder.label });
	}

	private addManualField(placeholder: RichTextEditorPlaceholder): void {
		const label = this.sanitizeLabel(placeholder.label);
		this.appendToken({ type: "token", kind: "ManualField", raw: `{{${label}.ManualField-${placeholder.id}}}`, label });
	}

	// Manual fields can be created on the fly (matching the rich-text body editor): derive a
	// camelCase key from the typed label and insert it as a manual-field token.
	private createManualField(label: string): void {
		const trimmed = (label ?? "").trim();
		if (!trimmed) {
			return;
		}
		const key = trimmed
			.replace(/(?:^\w|[A-Z]|\b\w)/g, (letter, index) => (index === 0 ? letter.toLowerCase() : letter.toUpperCase()))
			.replace(/\s+/g, "");
		this.addManualField({ id: key, label: trimmed });
	}

	private addCustomField(placeholder: RichTextEditorPlaceholder): void {
		const label = this.sanitizeLabel(placeholder.label);
		this.appendToken({ type: "token", kind: this.customFieldTokenKind as TokenKind, raw: `{{${label}.${this.customFieldTokenKind}-${placeholder.id}}}`, label });
	}

	private addAreaCustomField(placeholder: RichTextEditorPlaceholder): void {
		const label = this.sanitizeLabel(placeholder.label);
		this.appendToken({ type: "token", kind: "AreaCustomField", raw: `{{${label}.AreaCustomField-${placeholder.id}}}`, label });
	}

	private removeSegment(index: number): void {
		this.segments.splice(index, 1);
		this.normalizeSegments();
		this.emit();
	}

	// Every text segment renders its own input, so the text either side of a removed chip would
	// otherwise be left as two segments and the field would visibly split in two - with no way to
	// move a cursor across the join. Each further removal splits it again. Merging neighbours back
	// into one segment cannot change the value: buildRaw() joins the same characters in the same
	// order either way.
	private normalizeSegments(): void {
		const merged: Segment[] = [];
		this.segments.forEach((segment) => {
			const previous = merged[merged.length - 1];
			if (segment.type === "text" && previous && previous.type === "text") {
				previous.text = (previous.text ?? "") + (segment.text ?? "");
				return;
			}
			merged.push(segment);
		});

		// Always keep a trailing text segment so the operator can continue typing after a chip.
		if (merged.length === 0 || merged[merged.length - 1].type !== "text") {
			merged.push({ type: "text", text: "" });
		}

		this.segments = merged;
	}

	private chipClass(kind: TokenKind): string {
		switch (kind) {
			case "ManualField":
				return "token-chip token-manual";
			case "CustomField":
				return "token-chip token-custom";
			// Area fields get their own colour: the tenant form offers both custom-field
			// pickers at once, so sharing the navy chip would make the two indistinguishable.
			case "AreaCustomField":
				return "token-chip token-area-custom";
			default:
				return "token-chip token-placeholder";
		}
	}
}
